/**
 * iter693 — Extracted from PrintersSettingsPage.jsx.
 *
 * iPad-only printing options dialog. Surfaces the 2 ways an iPad can
 * print without a Bridge or a USB cable:
 *   1. Auto-iPrint (Share Sheet flow) — needs Epson iPrint installed.
 *   2. ePOS-Print Direct — silent print over HTTPS to the printer's
 *      built-in mini server. Most reliable; needs one-time cert accept.
 *
 * State is OWNED by the parent (PrintersSettingsPage) so that the
 * sibling "Accept all certs" and "ePOS confirm" dialogs can be opened
 * from here. Pure presentation + thin event handlers.
 *
 * Props are intentionally numerous (8) because this dialog is a thin
 * extraction — the goal is line-count reduction in PrintersSettingsPage,
 * NOT a behavior change. Subsequent passes can tighten the interface.
 */
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { printViaEposDirect, openPrinterCertPage } from '@/lib/eposPrint';

export default function IPadOptionsDialog({
  open,
  onOpenChange,
  printers,
  eposTestState,
  setEposTestState,
  setIpadVerifyState,
  setAcceptAllIndex,
  setAcceptAllOpen,
  setEposConfirmOpen,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90dvh] overflow-y-auto" data-testid="ipad-options-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span className="text-lg">📱</span>
            iPad printing options
          </DialogTitle>
          <p className="text-xs text-muted-foreground mt-1">
            Configure how iPads at this branch print receipts. Settings persist per device.
          </p>
        </DialogHeader>
        <div className="space-y-3 pt-2">
          {/* Auto-open Epson iPrint after payment (Share Sheet flow). */}
          <div className="rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 flex items-center justify-between gap-3" data-testid="auto-iprint-row">
            <div className="text-xs text-sky-900">
              <p className="font-semibold flex items-center gap-1.5">
                <span>📱</span> Auto-open Epson iPrint after payment
              </p>
              <p className="text-[11px] text-sky-800 mt-0.5">
                When a cashier completes an order, the iPad&apos;s Share Sheet pops up automatically — they just tap <strong>iPrint</strong> → <strong>Print</strong>. Requires Epson iPrint installed from App Store.
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer shrink-0">
              <input
                type="checkbox"
                className="sr-only peer"
                checked={(typeof window !== 'undefined' && localStorage.getItem('auto_iprint_on_pay') === '1')}
                onChange={(e) => {
                  if (e.target.checked) {
                    localStorage.setItem('auto_iprint_on_pay', '1');
                    toast.success('Auto-iPrint enabled on this iPad');
                  } else {
                    localStorage.removeItem('auto_iprint_on_pay');
                    toast.info('Auto-iPrint disabled on this iPad');
                  }
                  // Force a re-render so the checkbox UI matches storage.
                  setIpadVerifyState((s) => ({ ...s }));
                }}
                data-testid="auto-iprint-toggle"
              />
              <div className="w-10 h-5 bg-zinc-300 rounded-full peer peer-checked:bg-sky-600 after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-5"></div>
            </label>
          </div>

          {/* iter669 — ePOS-Print Direct (iPad). Silent print via the
              printer's built-in HTTP endpoint. Zero hardware, zero apps,
              zero taps after payment. Requires one-time cert acceptance
              on each iPad. */}
          <div className="rounded-lg border-2 border-emerald-300 bg-emerald-50 px-3 py-2.5 space-y-2" data-testid="epos-direct-row">
            <div className="flex items-center justify-between gap-3">
              <div className="text-xs text-emerald-900">
                <p className="font-bold flex items-center gap-1.5">
                  <span>⚡</span> ePOS-Print Direct (recommended for iPad)
                </p>
                <p className="text-[11px] text-emerald-800 mt-0.5">
                  Sends receipts straight to the printer&apos;s built-in HTTP service. <strong>Truly silent, zero taps after payment.</strong> No Bridge, no iPrint, no AirPrint needed. Same engine TM Utility uses internally.
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer shrink-0">
                <input
                  type="checkbox"
                  className="sr-only peer"
                  checked={(typeof window !== 'undefined' && localStorage.getItem('epos_direct_on_pay') === '1')}
                  onChange={(e) => {
                    if (e.target.checked) {
                      localStorage.setItem('epos_direct_on_pay', '1');
                      toast.success('ePOS Direct enabled on this iPad');
                    } else {
                      localStorage.removeItem('epos_direct_on_pay');
                      toast.info('ePOS Direct disabled on this iPad');
                    }
                    setEposTestState((s) => ({ ...s }));
                  }}
                  data-testid="epos-direct-toggle"
                />
                <div className="w-10 h-5 bg-zinc-300 rounded-full peer peer-checked:bg-emerald-600 after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-5"></div>
              </label>
            </div>

            {/* Setup checklist + test button */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-1.5 border-t border-emerald-200">
              <div className="text-[11px] text-emerald-900 space-y-1">
                <p className="font-semibold">📋 One-time setup per iPad:</p>
                <ol className="list-decimal ml-4 space-y-0.5">
                  <li>Configure your printer below with its IP (e.g. 192.168.1.237).</li>
                  <li>Tap <strong>&ldquo;Accept printer cert&rdquo;</strong> below → Safari opens the printer&apos;s admin → tap <strong>Show Details → Visit this website → Visit Website</strong>.</li>
                  <li>Tap <strong>&ldquo;Test ePOS Direct&rdquo;</strong> to fire a silent test print.</li>
                  <li>If the test prints → flip the toggle ON. Done.</li>
                </ol>
                {/* iter674 — Debug toggle. Hidden by default. When ON, every
                    payment surfaces a sticky 8s toast showing the exact
                    print decision path (printer count, IP, fetch result).
                    Useful when 1 branch of 10 misbehaves. */}
                <div className="flex items-center gap-2 mt-2 pt-1.5 border-t border-emerald-200">
                  <label className="flex items-center gap-1.5 cursor-pointer text-emerald-700">
                    <input
                      type="checkbox"
                      className="h-3.5 w-3.5"
                      checked={(typeof window !== 'undefined' && localStorage.getItem('epos_debug') === '1')}
                      onChange={(e) => {
                        if (e.target.checked) {
                          localStorage.setItem('epos_debug', '1');
                          toast.info('Debug toasts enabled. Complete an order to see the decision path.');
                        } else {
                          localStorage.removeItem('epos_debug');
                          toast.info('Debug toasts disabled.');
                        }
                        setEposTestState((s) => ({ ...s }));
                      }}
                      data-testid="epos-debug-toggle"
                    />
                    <span className="text-[10px]">🐛 Show per-payment debug toast</span>
                  </label>
                </div>
              </div>
              <div className="flex flex-col gap-1.5">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="h-8 rounded-md border-emerald-400 text-emerald-700 hover:bg-emerald-100 text-xs"
                  disabled={!printers?.length}
                  onClick={() => {
                    const ip = printers?.find((p) => (p.type || 'receipt') === 'receipt')?.ip_address
                      || printers?.[0]?.ip_address;
                    if (!ip) {
                      toast.error('Add a receipt printer first.');
                      return;
                    }
                    openPrinterCertPage(ip);
                  }}
                  data-testid="epos-direct-accept-cert"
                  title="Opens the printer's HTTPS page so Safari can save the self-signed certificate"
                >
                  🔐 Accept printer cert
                </Button>
                {/* iter676 — One-shot "Accept ALL" button for branches
                    with multiple printers (receipt + kitchen + bar). */}
                {printers?.filter(p => p.ip_address).length > 1 && (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-8 rounded-md border-emerald-500 text-emerald-800 hover:bg-emerald-100 text-xs"
                    onClick={() => {
                      setAcceptAllIndex(0);
                      setAcceptAllOpen(true);
                    }}
                    data-testid="epos-accept-all-certs"
                    title="Walks you through accepting the HTTPS cert for every printer with an IP at this branch — one at a time."
                  >
                    🔐🔐 Accept ALL ({printers.filter(p => p.ip_address).length})
                  </Button>
                )}
                <Button
                  type="button"
                  size="sm"
                  className="h-8 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-xs"
                  disabled={eposTestState.loading || !printers?.length}
                  onClick={async () => {
                    const receiptPrinter = printers?.find((p) => (p.type || 'receipt') === 'receipt') || printers?.[0];
                    if (!receiptPrinter?.ip_address) {
                      toast.error('No receipt printer with an IP found.');
                      return;
                    }
                    setEposTestState({ loading: true, result: null, error: null });
                    const sample = {
                      company: 'Smarto',
                      title: 'ePOS DIRECT TEST',
                      order_number: 'TEST-' + Date.now().toString().slice(-4),
                      date: new Date().toLocaleString(),
                      items: [{ qty: 1, name: 'ePOS Direct probe', subtotal: 0 }],
                      subtotal: 0, tax: 0, total: 0, currency: '',
                      footer: 'If this slip prints, ePOS Direct is working!',
                    };
                    const r = await printViaEposDirect(receiptPrinter, sample);
                    if (r.ok) {
                      setEposTestState({ loading: false, result: r, error: null });
                      toast.success(`Test fired to ${r.ip} (devid="${r.deviceId}") — check the printer!`);
                      // iter670 — open the "did the slip print?" confirm
                      // popup ~1.5s later so the user has time to glance
                      // at the printer.
                      setTimeout(() => setEposConfirmOpen(true), 1500);
                    } else {
                      setEposTestState({ loading: false, result: null, error: r });
                      toast.error(r.msg || 'Test failed.');
                    }
                  }}
                  data-testid="epos-direct-test"
                >
                  {eposTestState.loading ? <><Loader2 size={12} className="animate-spin mr-1" />Firing…</> : '🧪 Test ePOS Direct'}
                </Button>
                {eposTestState.error && (
                  <div className="text-[10px] text-rose-900 bg-rose-50 border border-rose-200 rounded p-2 leading-snug space-y-1.5" data-testid="epos-direct-error">
                    <p>{eposTestState.error.msg}</p>
                    {/* iter671 — Guided cert-acceptance dance: when the
                        fetch failed (almost always cert-not-accepted on
                        first try), one-tap CTA opens the printer admin,
                        and on return we auto-prompt to re-run Test. */}
                    {eposTestState.error.error === 'fetch-failed' && (
                      <div className="flex flex-col sm:flex-row gap-1.5 pt-1 border-t border-rose-200">
                        <Button
                          type="button"
                          size="sm"
                          className="h-7 text-[10px] rounded-md bg-rose-600 hover:bg-rose-700 text-white"
                          onClick={() => {
                            const ip = printers?.find((p) => (p.type || 'receipt') === 'receipt')?.ip_address
                              || printers?.[0]?.ip_address;
                            if (!ip) { toast.error('No printer IP found.'); return; }
                            openPrinterCertPage(ip);
                            // After the user comes back, surface the retry CTA.
                            setTimeout(() => {
                              setEposTestState((s) => ({ ...s, awaitingRetry: true }));
                            }, 1500);
                          }}
                          data-testid="epos-direct-guided-accept"
                        >
                          1️⃣ Open printer & accept cert
                        </Button>
                        {eposTestState.awaitingRetry && (
                          <Button
                            type="button"
                            size="sm"
                            className="h-7 text-[10px] rounded-md bg-emerald-600 hover:bg-emerald-700 text-white"
                            disabled={eposTestState.loading}
                            onClick={async () => {
                              const receiptPrinter = printers?.find((p) => (p.type || 'receipt') === 'receipt') || printers?.[0];
                              if (!receiptPrinter?.ip_address) { toast.error('No printer IP found.'); return; }
                              setEposTestState({ loading: true, result: null, error: null });
                              const sample = {
                                company: 'Smarto',
                                title: 'ePOS DIRECT TEST (retry)',
                                order_number: 'TEST-' + Date.now().toString().slice(-4),
                                date: new Date().toLocaleString(),
                                items: [{ qty: 1, name: 'ePOS Direct probe', subtotal: 0 }],
                                subtotal: 0, tax: 0, total: 0, currency: '',
                                footer: 'If this slip prints, ePOS Direct is working!',
                              };
                              const r = await printViaEposDirect(receiptPrinter, sample);
                              if (r.ok) {
                                setEposTestState({ loading: false, result: r, error: null });
                                toast.success(`Test fired to ${r.ip} (devid="${r.deviceId}") — check the printer!`);
                                setTimeout(() => setEposConfirmOpen(true), 1500);
                              } else {
                                setEposTestState({ loading: false, result: null, error: r });
                                toast.error(r.msg || 'Test failed again.');
                              }
                            }}
                            data-testid="epos-direct-guided-retry"
                          >
                            2️⃣ I accepted — retry Test
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                )}
                {eposTestState.result && (
                  <p className="text-[10px] text-emerald-700 font-semibold" data-testid="epos-direct-success">
                    ✓ Request reached <code>{eposTestState.result.ip}</code> using <code>devid={eposTestState.result.deviceId}</code>. Look for the slip!
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)} data-testid="ipad-options-close">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
